/**
 * Générateur — Il résout des problèmes modélisés par des fonctions en
 * utilisant un ou plusieurs modes de représentation.
 * Attendu ID : 230 | Niveau : 3ème | Domaine : Fonctions (d13, O18)
 *
 * Source doc prof (B3S7). Plan auto-validé 2026-07-16 (TODO §6) :
 * « tarifs » (constante / linéaire / affine confrontées : tableau + seuil),
 * « intersection » (résoudre f(x) = g(x) par le calcul, valeur exacte),
 * « benefice » (recette linéaire, bénéfice en un point). Les tracés/lectures
 * graphiques du doc sont portés par le calcul (adaptation signalée : la
 * morale du doc — « valeur approchée graphique vs valeur exacte algébrique »
 * — est conservée dans les consignes).
 */

import { randomChoice, randInt, tex, makeTexteTrou } from '../../../utils';
import { frNb, decFrag } from './fonctions';

const ATTENDU_ID = 230;
const L_TARIFS = 'Comparer trois tarifs (constante, linéaire, affine)';
const L_INTERSECTION = 'Résoudre f(x) = g(x) par le calcul';
const L_BENEFICE = 'Recette, coût, bénéfice';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « tarifs » ═════════

const genTarifsTrous = () => {
    // A = F fixe ; B = u·x ; C = c + v·x — seuil (F − c)/v ENTIER par construction.
    let F; let u5; let c; let v5; let seuil;
    for (let essai = 0; essai < 80; essai++) {
        v5 = randomChoice([5, 10, 15]);                    // v en centièmes (0,05 / 0,10 / 0,15)
        u5 = v5 + randomChoice([5, 10]);                   // B plus pentu que C
        c = randInt(5, 12);
        seuil = randomChoice([120, 150, 200, 220, 250, 300]);
        F = c + (v5 * seuil) / 100;
        if (Number.isInteger(F) && F >= 10 && F <= 60) break;
        F = 0;
    }
    if (!F) { F = 19; u5 = 10; c = 8; v5 = 5; seuil = 220; }
    const n = randomChoice([100, 200]);
    const bN = (u5 * n) / 100; const cN = c + (v5 * n) / 100;
    const phrase = `$\\text{Pour } ${n} \\text{ élèves : } A = {{${F}}} \\text{ €} \\qquad`
        + ` B = {{${frNb(bN)}}} \\text{ €} \\qquad C = {{${frNb(cN)}}} \\text{ €}$\n`
        + `$\\text{A devient le plus intéressant au-delà de } {{${seuil}}} \\text{ élèves}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un logiciel propose trois tarifs annuels : A = ${F} € quel que soit le nombre d'élèves ; `
        + `B = ${frNb(u5 / 100)} € par élève ; C = ${c} € plus ${frNb(v5 / 100)} € par élève.</p>`
        // « (résous 19 = 8 + 0,05x) » donnait la mise en équation.
        + `<p><b>Calcule les trois tarifs pour ${n} élèves, puis trouve à partir de combien d'élèves `
        + 'le tarif A devient plus intéressant que le tarif C.</b></p>',
        { ...opts(L_TARIFS), mode: 'saisie' });
    payload._meta.params = { F, u5, c, v5, seuil, n };
    return payload;
};

// ═════════ « intersection » — f(x) = g(x) ═════════

const genIntersectionTrous = () => {
    // f = a1x + b1, g = a2x + b2 : x* = (b2 − b1)/(a1 − a2) exact en dixièmes.
    let a110; let b1; let a210; let b2; let x10 = null;
    for (let essai = 0; essai < 80; essai++) {
        a110 = randomChoice([12, 15, 17, 20, 25]);         // a1 en dixièmes
        a210 = randomChoice([5, 8, 10, 12]);
        if (a110 === a210) continue;
        b1 = -randInt(1, 5); b2 = randInt(1, 5);           // b1 < 0 < b2 → x* > 0
        // x*·10 = 100(b2 − b1)/(a110 − a210) : ENTIER exigé → x* exact au dixième.
        const num = (b2 - b1) * 100;
        if (num % (a110 - a210) !== 0) continue;
        x10 = num / (a110 - a210);
        if (x10 >= 5 && x10 <= 200) break;
        x10 = null;
    }
    if (x10 === null) { a110 = 17; a210 = 12; b1 = -2; b2 = 1; x10 = 60; } // repli : x* = 6
    const xEt = x10 / 10;                                  // x* réel
    const y = (a110 / 10) * xEt + b1;
    const fAff = `${decFrag(a110 / 10)}x ${b1 < 0 ? '-' : '+'} ${Math.abs(b1)}`;
    const gAff = `${decFrag(a210 / 10)}x ${b2 < 0 ? '-' : '+'} ${Math.abs(b2)}`;
    const phrase = `$x = {{${frNb(xEt)}}} \\qquad \\text{ordonnée commune : } {{${frNb(Math.round(y * 100) / 100)}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`f(x) = ${fAff}`)} et ${tex(`g(x) = ${gAff}`)}. Leurs droites se coupent `
        + 'en un point K : le calcul donne la valeur EXACTE, là où une lecture à l\'œil ne donnerait '
        + 'qu\'une estimation.</p>'
        + `<p><b>Résous ${tex('f(x) = g(x)')}, puis calcule l'ordonnée de K.</b> `
        + 'Utilise une virgule pour les décimaux.</p>',
        { ...opts(L_INTERSECTION), mode: 'saisie' });
    payload._meta.params = { a110, b1, a210, b2, xEt };
    return payload;
};

// ═════════ « benefice » ═════════

const genBeneficeTrous = () => {
    const p = randomChoice([25, 30, 34, 40, 45]);          // prix de vente €/kg
    const x0 = randomChoice([10, 15, 20]);
    const c0 = randInt(3, 9) * 20;                         // coût pour x0 kg (donné)
    const recette = p * x0;
    const benefice = recette - c0;
    if (benefice <= 0) return genBeneficeTrous.repli();
    const phrase = `$R(x) = {{${p}}}x \\qquad \\text{recette pour } ${x0} \\text{ kg : } {{${recette}}} \\text{ €}`
        + ` \\qquad \\text{bénéfice : } {{${benefice}}} \\text{ €}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un producteur vend sa production ${p} € le kilogramme. Produire ${x0} kg lui coûte ${c0} €.</p>`
        // « (bénéfice = recette − coût) » donnait la relation à établir.
        + `<p><b>Exprime la recette ${tex('R(x)')} pour x kg vendus, puis calcule la recette et le `
        + `bénéfice pour ${x0} kg.</b></p>`,
        { ...opts(L_BENEFICE), mode: 'saisie' });
    payload._meta.params = { p, x0, c0, recette, benefice };
    return payload;
};
genBeneficeTrous.repli = () => {
    const payload = makeTexteTrou(
        '$R(x) = {{34}}x \\qquad \\text{recette pour } 10 \\text{ kg : } {{340}} \\text{ €}'
        + ' \\qquad \\text{bénéfice : } {{240}} \\text{ €}$',
        '<p>Un producteur vend sa production 34 € le kilogramme. Produire 10 kg lui coûte 100 €.</p>'
        + `<p><b>Exprime la recette ${tex('R(x)')} pour x kg vendus, puis calcule la recette et le `
        + 'bénéfice pour 10 kg.</b></p>',
        { ...opts(L_BENEFICE), mode: 'saisie' });
    payload._meta.params = { repli: true };
    return payload;
};

const GENERATORS = {
    tarifs_texte_trou_saisie: genTarifsTrous,
    intersection_texte_trou_saisie: genIntersectionTrous,
    benefice_texte_trou_saisie: genBeneficeTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'tarifs', label: L_TARIFS, formatsList: buildFormats('tarifs') },
    { id: 'intersection', label: L_INTERSECTION, formatsList: buildFormats('intersection') },
    { id: 'benefice', label: L_BENEFICE, formatsList: buildFormats('benefice') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
